<template>
  <el-row :gutter="20">
    <el-col :span="12" :offset="6">
      <h2 class="hotel-name-header">{{ hotel.name }}</h2>
    </el-col>
    <el-col :span="12" :offset="6">
      <el-form ref="form" label-width="0px">
        <div class="label-contract">
          {{ $t('refCode') }}:
        </div>
        <el-form-item>
          <el-input v-model="dynamicRefCode"
                    :disabled="autoRefCode"
                    class="text-uppercase"></el-input>
          <el-checkbox v-model="autoRefCode" v-if="refCodeFormat.trim() !== ''"
          @change="onAutoGenerateRefCodeCheckChange()">Auto generate Ref Code</el-checkbox>
        </el-form-item>

        <div class="label-contract">
          {{ $t('contractValidFrom') }}:
        </div>
        <el-form-item>
          <el-date-picker v-model="contractValidPeriod"
                          type="daterange"
                          format="d MMM yyyy"
                          value-format="yyyy-MM-dd"
                          start-placeholder="From"
                          end-placeholder="To"
                          style="width: 100%;"
                          @change="onGeneralDataChange">
          </el-date-picker>
        </el-form-item>
        <div class="label-contract">
          {{ $t('salePerson') }}:
        </div>
        <el-form-item>
          <el-select
            v-model="selectedSalePersonId"
            :placeholder="$t('pleaseSelectSalePerson')"
            style="width: 100%"
            @change="onGeneralDataChange">
            <el-option
              v-for="(salePerson, index) in displaySalePersonList"
              :key="index"
              :label="`${salePerson.title} ${salePerson.firstName} ${salePerson.lastName}`"
              :value="salePerson.id">
            </el-option>
          </el-select>
          <div>
            <el-checkbox v-model="isAddSignature" :disabled="signatureUrl === ''" @change="onGeneralDataChange">
              <span>{{ $t('addSignatureInContract') }}</span>
            </el-checkbox>
            <div v-if="isAddSignature === true && signatureUrl !== ''">
              <img height="80px" alt="Signature" :src="signatureUrl">
            </div>
          </div>
          <div class="contract-data-version" style="margin: 0" v-if="isUpdatedSalePerson()">
            <div class="contract-data-updated">
              <div style="padding: 10px 0 0 20px">
                <strong>*{{$t('updatedSalePersonMessage')}}</strong>
              </div>
              <el-radio-group style="padding-left: 40px" v-model="salePersonDataVersion" fill="#ff0000" @change="onGeneralDataChange">
                <div class="contract-data-version-radio">
                  <el-radio v-model="salePersonDataVersion" :label="DATA_VERSION.SELECTED">{{$t('selectedVersion')}} ({{bindUpdatedAt(tmpSelectedSalePerson.updatedAt)}})</el-radio>
                </div>
                <div class="contract-data-version-radio">
                  <el-radio v-model="salePersonDataVersion" :label="DATA_VERSION.LATEST">{{$t('latestVersion')}} ({{bindLatestUpdatedAt(contractDetail.salePerson.id)}})</el-radio>
                </div>
              </el-radio-group>
            </div>
          </div>
          <div class="contract-data-version" style="margin: 0" v-if="isDeletedSalePerson()">
            <div class="contract-data-deleted">
              <div style="padding: 10px 0 0 20px">
                <strong style="color: #dd3333">*{{$t('deletedSalePersonMessage')}}</strong>
              </div>
              <el-radio-group style="padding-left: 40px" v-model="selectedSalePersonId" fill="#ff0000" >
                <el-radio :label="selectedSalePersonId" class="contract-data-version-radio">{{$t('selectedVersion')}} ({{bindUpdatedAt(tmpSelectedSalePerson.updatedAt)}})</el-radio>
              </el-radio-group>
            </div>
          </div>
        </el-form-item>
        <div class="label-contract">
          {{ $t('contractRemark') }}:
        </div>
        <el-form-item>
          <editor-component v-model="contractRemark"></editor-component>
        </el-form-item>
        <div class="label-contract">
          <el-checkbox v-model="isDisplayInternalNote"></el-checkbox>
          {{ $t('internalNote') }}:
        </div>
        <el-form-item v-show="isDisplayInternalNote">
          <el-input
            type="textarea"
            :rows="5"
            v-model="internalNote"
            @change="onGeneralDataChange"></el-input>
        </el-form-item>
      </el-form>
    </el-col>
  </el-row>
</template>

<script>
import i18nMessages from '@/translations/Contract/components/TabContractGeneral.i18n'
import EditorComponent from '@/components/EditorComponent'
import { REMARK_TYPE } from '@/configs/constant'
import { getISODateFormat, getContractRemark } from '@/helpers'
import { get, cloneDeep } from 'lodash'
import { genRefCode, applyNewRemark } from '@/domains/contract-domain'
import { dateFormat } from '@/helpers/datetime'
import { isUpdatedData, isDeletedData, DATA_VERSION } from '@/services/version-service'

export default {
  name: 'TabGeneralComponent',
  i18n: { messages: i18nMessages },
  components: { EditorComponent },
  model: {
    prop: 'contractDetail',
    event: 'onGeneralDetailChange'
  },
  props: {
    hotel: {
      type: Object,
      default: () => {
        return {}
      }
    },
    salePersonList: {
      type: Array,
      default: () => {
        return []
      }
    },
    contractDetail: {
      type: Object,
      default: () => {
        return {}
      }
    },
    refCodeFormat: {
      type: String,
      default: ''
    },
    marketList: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  created: function () {
    const autoRefCode = _isAutoRefCode(this.refCodeFormat, this.contractDetail)
    const salePersonId = get(this.contractDetail, 'salePerson.id', '')
    const salePerson = get(this.contractDetail, 'salePerson', {})
    const internalNote = get(this.contractDetail, 'internalNote', '')
    const refCode = _initRefCode(this.contractDetail, this.refCodeFormat, this.marketList)
    const isAddSignature = Boolean(get(salePerson, 'isAddSignature', 0))

    let contractValidStart = get(this.contractDetail, 'startDate', '')
    let contractValidEnd = get(this.contractDetail, 'endDate', '')

    if (contractValidStart === null) {
      contractValidStart = ''
    }
    if (contractValidEnd === null) {
      contractValidEnd = ''
    }

    const startDate = getISODateFormat(new Date(contractValidStart))
    const endDate = getISODateFormat(new Date(contractValidEnd))

    this.tmpSelectedSalePerson = cloneDeep(salePerson)
    this.tmpSelectedSalePersonId = get(this.tmpSelectedSalePerson, 'id', '')

    this.signatureUrl = get(salePerson, 'signatureUrl', '')
    this.isAddSignature = (this.tmpSelectedSalePersonId === '') ? true : isAddSignature

    this.contractValidPeriod.push(startDate, endDate)
    this.refCode = refCode
    this.selectedSalePersonId = salePersonId
    this.internalNote = internalNote
    this.autoRefCode = autoRefCode
    this.contractDetail.refCode = refCode
    this.contractDetail.autoRefCode = autoRefCode

    if (internalNote !== '') {
      this.isDisplayInternalNote = true
    }

    const salePersonUpdatedAt = get(salePerson, 'updatedAt', '')
    if (this.tmpSelectedSalePersonId !== '' && salePersonUpdatedAt === '') {
      const lastedSalePerson = this.salePersonList.find(item => item.id === salePersonId)

      if (lastedSalePerson !== undefined) {
        this.contractDetail.salePerson = cloneDeep(lastedSalePerson)
        this.tmpSelectedSalePerson = cloneDeep(lastedSalePerson)
        this.tmpSelectedSalePersonId = get(this.tmpSelectedSalePerson, 'id', '')
        this.signatureUrl = get(lastedSalePerson, 'signatureUrl', '')
        this.isAddSignature = false

        this.onGeneralDataChange()
      }
    }

    this._prepareSalePersonList()
  },
  data () {
    return {
      DATA_VERSION: DATA_VERSION,
      contractValidPeriod: [],
      refCode: '',
      selectedSalePersonId: '',
      remarkList: [],
      internalNote: '',
      displaySalePersonList: [],
      autoRefCode: false,
      isDisplayInternalNote: false,
      signatureUrl: '',
      isAddSignature: true,
      salePersonDataVersion: DATA_VERSION.SELECTED,
      tmpSelectedSalePersonId: '',
      tmpSelectedSalePerson: {}
    }
  },
  computed: {
    dynamicRefCode: {
      get () {
        if (this.autoRefCode === true) {
          return genRefCode(this.contractDetail, this.refCodeFormat, this.marketList)
        }

        return this.refCode
      },
      set (newVal) {
        this.refCode = newVal
        this.onGeneralDataChange()
      }
    },
    contractRemark: {
      get () {
        const remark = getContractRemark(this.contractDetail, REMARK_TYPE.CONTRACT)

        return remark
      },
      set (newVal) {
        this.newRemark = {
          remarkType: REMARK_TYPE.CONTRACT,
          content: newVal
        }

        this.onGeneralDataChange()
      }
    }
  },
  methods: {
    onGeneralDataChange: function () {
      const remarkList = applyNewRemark(this.contractDetail, this.newRemark)
      const startDateIndex = 0
      const endDateIndex = 1
      const salePerson = this._getUpdateSalePerson()
      const periodStart = _getPeriodDate(this.contractValidPeriod, startDateIndex)
      const periodEnd = _getPeriodDate(this.contractValidPeriod, endDateIndex)

      const generalDetail = {
        refCode: this.refCode.toUpperCase(),
        autoRefCode: this.autoRefCode,
        startDate: periodStart,
        endDate: periodEnd,
        salePerson: (salePerson === undefined) ? {} : salePerson,
        internalNote: this.internalNote
      }
      const newRemark = {
        remarkList: remarkList
      }
      const contractDetail = Object.assign({}, this.contractDetail, generalDetail, newRemark)

      if (this.autoRefCode === true) {
        this.refCode = genRefCode(contractDetail, this.refCodeFormat, this.marketList)
      }

      const newRefCode = {
        refCode: this.refCode.toUpperCase()
      }
      const newContractDetail = Object.assign({}, contractDetail, newRefCode)
      this.$emit('onGeneralDetailChange', newContractDetail)
    },
    isDisabledRefCode: function () {
      const contractId = get(this.contractDetail, 'id', '')

      return (contractId !== '')
    },
    onAutoGenerateRefCodeCheckChange: function () {
      this.onGeneralDataChange()
    },
    isUpdatedSalePerson: function () {
      if (this.selectedSalePersonId === '') {
        return false
      }

      if (this.selectedSalePersonId !== this.tmpSelectedSalePersonId) {
        return false
      }

      const isUpdated = isUpdatedData(this.salePersonList, this.tmpSelectedSalePerson)
      return isUpdated
    },
    isDeletedSalePerson: function () {
      if (this.selectedSalePersonId === '') {
        return false
      }

      if (this.selectedSalePersonId !== this.tmpSelectedSalePersonId) {
        return false
      }

      const isDeleted = isDeletedData(this.salePersonList, this.tmpSelectedSalePersonId)
      return isDeleted
    },
    bindLatestUpdatedAt: function (salePersonId) {
      const salePerson = this.salePersonList.find(item => item.id === salePersonId)

      if (salePerson !== undefined) {
        const date = new Date(salePerson.updatedAt)
        return dateFormat.long(date)
      }
    },
    bindUpdatedAt: function (updatedDate) {
      if (updatedDate === undefined) {
        return '-'
      }

      const date = new Date(updatedDate)
      return dateFormat.long(date)
    },
    _prepareSalePersonList: function () {
      const currentSalePersonId = get(this.contractDetail, 'salePerson.id', '')
      let currentSalePerson = get(this.contractDetail, 'salePerson', {})

      const salePersonList = this.salePersonList.map((item) => {
        if (Number(item.id) === Number(currentSalePersonId)) {
          return currentSalePerson
        }

        return item
      })

      if (currentSalePersonId !== '') {
        const salePerson = salePersonList.find(item => item.id === currentSalePersonId)

        if (salePerson === undefined) {
          salePersonList.push(currentSalePerson)
        }
      }

      this.displaySalePersonList = salePersonList.sort((prev, next) => {
        return (prev.firstName < next.firstName) ? -1 : 1
      })
    },
    _getUpdateSalePerson: function () {
      let salePersonResult = this.displaySalePersonList.find(salePerson => salePerson.id === this.selectedSalePersonId)
      this.signatureUrl = get(salePersonResult, 'signatureUrl', '')

      if (this.salePersonDataVersion === DATA_VERSION.LATEST) {
        salePersonResult = this.salePersonList.find(salePerson => salePerson.id === this.selectedSalePersonId)
        this.signatureUrl = get(salePersonResult, 'signatureUrl', '')
      }

      const isAddSignature = {
        isAddSignature: (this.signatureUrl === '') ? false : this.isAddSignature
      }

      salePersonResult = Object.assign({}, salePersonResult, isAddSignature)
      return salePersonResult
    }
  }
}

function _isAutoRefCode (refCodeFormat, contractDetail) {
  const contractId = get(contractDetail, 'id', '')

  if (contractId === '' && refCodeFormat.trim() !== '') {
    return true
  }

  const isAutoRefCode = Boolean(get(contractDetail, 'autoRefCode', 0))

  return isAutoRefCode
}

function _initRefCode (contractDetail, refCodeFormat, marketList) {
  let refCode = get(contractDetail, 'refCode', '')
  const isAutoRefCode = _isAutoRefCode(refCodeFormat, contractDetail)

  if (refCode === '' && isAutoRefCode) {
    refCode = genRefCode(contractDetail, refCodeFormat, marketList)
  }

  return refCode
}

function _getPeriodDate (contractValidPeriod, periodIndex) {
  const validPeriod = get(contractValidPeriod, `${periodIndex}`, 'NaN-NaN-NaN')
  const periodDate = new Date(validPeriod)

  return (isNaN(periodDate)) ? '' : validPeriod
}
</script>

<style>
  .hotel-name-header {
    background-color: #f8f9fa;
    color: #2c3e50;
    text-align: center;
    line-height: 40px;
    font-weight: bold;
    font-size: 20px;
  }

  .label-contract {
    font-weight: bold;
    margin: 10px 0;
  }
</style>
